"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useKonamiCode } from "@/hooks/useKonamiCode";
import { PAUSE_3D } from "@/components/3D/Utils/postFxTuning";
import { bootChirp, audioContext, isMuted, setMuted, subscribeMuted, unlockAudio, getMusicTrack, setMusicTrack, subscribeMusicTrack } from "./sound";
type GameModule = typeof import("./game");
type Phase = "idle" | "boot" | "error" | "game";
const BOOT_LINES = ["CHEAT CODE ACCEPTED", "ACCESSING MASSIF DEEP SPACE NETWORK...", "640K MEMORY OK", "LOADING SCRUM.EXE..."];

// The game chunk (Phaser + content) is only requested after the Konami Code.
let loading: Promise<GameModule> | null = null;
const loadGame = () => loading ??= import("./game").catch(e => {
  loading = null; // allow RETRY
  throw e;
});
export default function ScrumNebulaLauncher() {
  const $ = _c(42);
  const [phase, setPhase] = useState("idle");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [lines, setLines] = useState(t0);
  const [artReady, setArtReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const overlay = useRef(null);
  const host = useRef(null);
  const game = useRef(null);
  const stopBoot = useRef(null);
  const active = phase !== "idle";
  const muted = useSyncExternalStore(subscribeMuted, isMuted, _temp);
  const musicTrack = useSyncExternalStore(subscribeMusicTrack, getMusicTrack, _temp2);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      stopBoot.current?.();
      stopBoot.current = null;
      audioContext()?.suspend().catch(_temp3);
      setPhase("idle");
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const close = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (overlay.current) {
        return;
      }
      const ctx = unlockAudio();
      if (ctx) {
        stopBoot.current = bootChirp(ctx);
      }
      setLines([]);
      setArtReady(false);
      setProgress(0);
      setPhase(_temp4);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const open = t2;
  useKonamiCode(open);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (process.env.NODE_ENV === "development" && (new URLSearchParams(window.location.search).has("konami") || new URLSearchParams(window.location.search).has("scrum-preview") || new URLSearchParams(window.location.search).has("scrum-room"))) {
        open();
      }
    };
    t4 = [open];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== phase) {
    t5 = () => {
      if (phase !== "boot") {
        return;
      }
      let alive = true;
      const visibility = _temp7;
      document.addEventListener("visibilitychange", visibility);
      if (document.hidden) {
        visibility();
      }
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const step = reduced ? 150 : 600;
      const timers = BOOT_LINES.map((_, i) => window.setTimeout(() => setLines(BOOT_LINES.slice(0, i + 1)), i * step));
      Promise.all([loadGame(), new Promise(r => window.setTimeout(r, BOOT_LINES.length * step))]).then(t7 => {
        const [mod] = t7;
        if (!alive) {
          return;
        }
        game.current = mod;
        setPhase("game");
      }).catch(() => {
        if (alive) {
          audioContext()?.suspend().catch(_temp8);
          setPhase("error");
        }
      });
      return () => {
        alive = false;
        timers.forEach(clearTimeout);
        document.removeEventListener("visibilitychange", visibility);
        stopBoot.current?.();
        stopBoot.current = null;
      };
    };
    t6 = [phase];
    $[5] = phase;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] !== phase) {
    t7 = () => {
      if (phase !== "game" || !host.current || !game.current) {
        return;
      }
      host.current.focus();
      return game.current.startGame(host.current, {
        onExit: close,
        onProgress: setProgress,
        onReady: () => setArtReady(true),
        onError: () => setPhase("error")
      });
    };
    t8 = [phase, close];
    $[8] = phase;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[11] !== active) {
    t9 = () => {
      if (!active || !overlay.current) {
        return;
      }
      const self = overlay.current;
      const prevFocus = document.activeElement as HTMLElement | null;
      const siblings = [...document.body.children].filter(el => el instanceof HTMLElement && el !== self && !el.inert);
      siblings.forEach(_temp9);
      const root = document.documentElement;
      const overflow = root.style.overflow;
      root.style.overflow = "hidden";
      PAUSE_3D.value = true;
      const onKey = e => {
        if (e.key !== "Escape") {
          return;
        }
        e.stopPropagation();
        close();
      };
      window.addEventListener("keydown", onKey, true);
      return () => {
        siblings.forEach(_temp10);
        if (root.style.overflow === "hidden") {
          root.style.overflow = overflow;
        }
        PAUSE_3D.value = false;
        window.removeEventListener("keydown", onKey, true);
        prevFocus?.focus();
      };
    };
    t10 = [active, close];
    $[11] = active;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  if (!active) {
    return null;
  }
  let t11;
  if ($[14] !== lines || $[15] !== phase) {
    t11 = phase === "game" ? <div ref={host} tabIndex={-1} className="flex size-full items-center justify-center outline-none" /> : <pre className="whitespace-pre-wrap p-6 text-sm leading-relaxed">{lines.join("\n")}{phase === "error" && "\n\nDISK READ ERROR\nABORT, RETRY, FAIL?"}</pre>;
    $[14] = lines;
    $[15] = phase;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== artReady || $[18] !== phase || $[19] !== progress) {
    t12 = phase === "game" && !artReady && <div className="absolute inset-0 flex items-center justify-center bg-black"><p role="status">LOADING SECTOR ART... {Math.round(progress * 100)}%</p></div>;
    $[17] = artReady;
    $[18] = phase;
    $[19] = progress;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== phase) {
    t13 = phase === "error" && <div className="flex gap-4 px-6"><button type="button" onClick={() => {
        const ctx_1 = unlockAudio();
        if (ctx_1) {
          stopBoot.current = bootChirp(ctx_1);
        }
        setLines([]);
        setArtReady(false);
        setProgress(0);
        setPhase("boot");
      }} className="underline">RETRY</button><button type="button" onClick={close} className="underline">ABORT</button></div>;
    $[21] = phase;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <option value="obsidian">Obsidian</option>;
    t15 = <option value="nebula">Nebula</option>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  let t16;
  if ($[25] !== musicTrack) {
    t16 = <label className="flex items-center gap-2">Game music<select aria-label="Music" value={musicTrack} onKeyDown={_temp11} onChange={_temp12} className="cursor-pointer bg-black uppercase hover:text-white">{t14}{t15}</select></label>;
    $[25] = musicTrack;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== muted) {
    t17 = () => setMuted(!muted);
    $[27] = muted;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = muted ? "Sound off [M]" : "Sound on [M]";
  let t19;
  if ($[29] !== muted || $[30] !== t17 || $[31] !== t18) {
    t19 = <button type="button" aria-pressed={muted} onClick={t17} className="hover:text-white">{t18}</button>;
    $[29] = muted;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <button type="button" onClick={close} className="hover:text-white">Exit [Esc]</button>;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t16 || $[35] !== t19) {
    t21 = <div className="absolute inset-x-4 top-4 flex flex-wrap justify-end gap-x-4 gap-y-2 text-xs uppercase tracking-widest">{t16}{t19}{t20}</div>;
    $[34] = t16;
    $[35] = t19;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t21) {
    t22 = <div ref={overlay} role="dialog" aria-modal="true" aria-label="The Secret of the SCRUM Nebula" className="fixed inset-0 z-[10000] bg-black font-secondary text-[#c0c0c0]">{t11}{t12}{t13}{t21}</div>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  return t22;
}
function _temp12(event_0) {
  return setMusicTrack(event_0.target.value === "nebula" ? "nebula" : "obsidian");
}
function _temp11(event) {
  return event.stopPropagation();
}
function _temp10(el_1) {
  return el_1.inert = false;
}
function _temp9(el_0) {
  return el_0.inert = true;
}
function _temp8() {}
function _temp7() {
  const ctx_0 = audioContext();
  if (!ctx_0) {
    return;
  }
  if (document.hidden) {
    ctx_0.suspend().catch(_temp5);
  } else {
    ctx_0.resume().catch(_temp6);
  }
}
function _temp6() {}
function _temp5() {}
function _temp4(p) {
  return p === "idle" ? "boot" : p;
}
function _temp3() {}
function _temp2() {
  return "obsidian";
}
function _temp() {
  return false;
}